import { useCallback, useEffect, useRef } from "react";
import { toast } from "sonner";

const DRAFTING_STOPPED_COPY =
  "Drafting stopped. Midship will draft the pending columns on your next message.";

const DRAFTING_CONNECTION_DROPPED_COPY =
  "The connection dropped, so drafting stopped. Midship will draft the pending columns on your next message.";

export interface DraftPendingColumnsCallbacks<P> {
  onPlanUpdated: (plan: P) => void;
  onComplete: () => void;
  onError: (error: string) => void;
  /** Replaces the error toast for a dropped connection. */
  onTransportError: (error: string) => void;
}

interface DraftPendingColumnsOnOpenOptions<P> {
  sessionId: string | null | undefined;
  seededColumnsToDraft: boolean;
  draftPendingColumns: (
    callbacks: DraftPendingColumnsCallbacks<P>,
  ) => Promise<void>;
  onPlanUpdated: (plan: P) => void;
  refetchSession: (options: { backgroundFetch: boolean }) => unknown;
}

/**
 * Starts a revise session's pending-column drafting once, as soon as the loaded
 * session says its seeded analytics columns still need drafted tests.
 *
 * Fires at most once per session id: the flag stays true after a failed or
 * stopped drafting turn, and re-renders or a background refetch must not start
 * the turn again. The columns are drafted on the user's next message.
 *
 * Returns `noticeIfDraftingStopped`, for the Stop handler to call before it
 * cancels the stream: it tells the user what happens to the columns when the
 * turn being stopped is this drafting turn, and does nothing otherwise.
 */
export const useDraftPendingColumnsOnOpen = <P>({
  sessionId,
  seededColumnsToDraft,
  draftPendingColumns,
  onPlanUpdated,
  refetchSession,
}: DraftPendingColumnsOnOpenOptions<P>) => {
  const startedForSessionIdRef = useRef<string | null>(null);
  const draftingInFlightRef = useRef(false);

  // A failed or dropped drafting turn leaves the session iterating, not
  // failed. Every ending refetches: a drop never reaches onComplete, and the
  // server may have saved the result just before it, which a stale plan would
  // let the next edit overwrite. After a server error frame onComplete
  // refetches too; the second read is a harmless duplicate.
  const startDrafting = useCallback(
    () =>
      draftPendingColumns({
        onPlanUpdated,
        onComplete: () => {
          refetchSession({ backgroundFetch: true });
        },
        onError: (error) => {
          console.error("Seeded column drafting error:", error);
          refetchSession({ backgroundFetch: true });
        },
        // The server stops drafting when its stream closes, so a drop is a
        // stop, and the browser's network message would misread it as a
        // failure.
        onTransportError: (error) => {
          console.error("Seeded column drafting connection dropped:", error);
          refetchSession({ backgroundFetch: true });
          toast.info(DRAFTING_CONNECTION_DROPPED_COPY);
        },
      }),
    [draftPendingColumns, onPlanUpdated, refetchSession],
  );

  useEffect(() => {
    if (!sessionId || !seededColumnsToDraft) {
      return;
    }
    if (startedForSessionIdRef.current === sessionId) {
      return;
    }
    startedForSessionIdRef.current = sessionId;
    draftingInFlightRef.current = true;
    startDrafting().finally(() => {
      draftingInFlightRef.current = false;
    });
  }, [sessionId, seededColumnsToDraft, startDrafting]);

  const noticeIfDraftingStopped = useCallback(() => {
    if (draftingInFlightRef.current) {
      toast.info(DRAFTING_STOPPED_COPY);
    }
  }, []);

  return { noticeIfDraftingStopped };
};
